import { Spinner, theme, EmptyState, Icon, IconDictionary } from '@ketch-com/deck'
import { useDataSentryEventsTrend } from 'api/dataSentry/queries/useDataSentryEventsTrend'
import { useDataSentryContext } from 'pages/dataSentry/context/DataSentryContext'
import React, { useMemo } from 'react'
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { CustomDateTick, CustomEventCountTicks } from './LineChartCustomTicks'
import { Box } from '@mui/material'

export const EventsOverTimeLineChart: React.FC = () => {
  const { selectedTimeRange, selectedPropertyCode } = useDataSentryContext()
  const { data: eventsTrend, isLoading } = useDataSentryEventsTrend({
    params: {
      'start-date': selectedTimeRange?.startDate,
      'end-date': selectedTimeRange?.endDate,
      'property-codes': selectedPropertyCode,
    },
  })
  const MaxGridEventTick = useMemo(() => {
    if (eventsTrend?.trend) {
      let trendMax = 100
      eventsTrend.trend.forEach(event => {
        if (event.count && event.count > trendMax) {
          trendMax = event.count
        }
      })
      const topTick = Math.ceil(trendMax / 100) * 100
      return [topTick, topTick / 2]
    }
    return [100, 50]
  }, [eventsTrend?.trend])

  return (
    <Box
      height="100%"
      borderRadius="0px 8px 8px 0px"
      sx={{
        backgroundColor: theme.palette.Black.o4,
        '& .recharts-wrapper .recharts-cartesian-grid-horizontal line:first-of-type': {
          strokeOpacity: 0,
        },
        '& .recharts-wrapper .recharts-cartesian-grid-horizontal line:last-of-type': {
          strokeOpacity: 0,
        },
        '& .recharts-wrapper .recharts-cartesian-grid-vertical line:first-of-type': {
          strokeOpacity: 0,
        },
        '& .recharts-wrapper .recharts-cartesian-grid-vertical line:last-of-type': {
          strokeOpacity: 0,
        },
      }}
    >
      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="100%" width="100%">
          <Spinner size="32px" thickness={2.5} />
        </Box>
      ) : null}

      {!isLoading && !eventsTrend?.trend?.length ? (
        <EmptyState
          size="small"
          colorTheme={theme.palette.Text.Secondary}
          sx={{ backgroundColor: theme.palette.Black.o8 }}
          customIcon={
            <Icon name={IconDictionary.FChart} width="48px" height="48px" iconColor={theme.palette.Black.o48} />
          }
          title="No Data"
        />
      ) : null}

      {!isLoading && eventsTrend?.trend?.length ? (
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={eventsTrend?.trend || []} syncId="anyId" margin={{ top: 20, right: 2, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              stroke={theme.palette.Black.o4}
              height={1}
              dataKey="date"
              tickLine={false}
              tick={<CustomDateTick />}
            />
            <YAxis
              stroke={theme.palette.Black.o4}
              tickLine={false}
              width={48}
              tick={<CustomEventCountTicks />}
              ticks={MaxGridEventTick}
            />
            <Tooltip />
            <Line type="monotone" dataKey="count" stroke={theme.palette.Text.Primary} />
          </LineChart>
        </ResponsiveContainer>
      ) : null}
    </Box>
  )
}
